import { Button } from '@/components/ui/button';
import { FC } from 'react';
import SettlementsElementSkeleton from '@/features/finances/components/Settlements/SettlementsElementSkeleton';
import { FinancesUtils } from '@/features/finances/utils/financesUtils';
import { cn } from '@/lib/utils';
import CreditCardIcon from '@/components/icons/CreditCardIcon';
import { t } from 'i18next';
import ArrowRightIcon from '@/components/icons/ArrowRightIcon';
import CurrencyValue from '../CurrencyValue';
import { CurrencyAssetType } from '@/utils/CurrencyHelper';

type Props = {
    label: 'property' | 'otherService';
    value?: string | number;
    icon: React.FC<React.SVGProps<SVGSVGElement>>;
    onClick?: () => void;
    balanceVisibility?: boolean;
    isLoading?: boolean;
    isMobile?: boolean;
    type?: 'select' | 'pay';
    asset?: CurrencyAssetType;
};

const SettlementsElement: FC<Props> = ({
    label,
    value,
    icon: IconComponent,
    onClick,
    balanceVisibility,
    isLoading,
    isMobile,
    type = 'select',
    asset
}) => {
    const { title, iconColor, iconBgColor } = FinancesUtils.getBalanceElementConfig(label);

    const hiddenBalance = (
        <div className={cn("h-6 flex items-center gap-1", isMobile && 'h-5')}>
            {Array.from({ length: 5 }).map((_, index) => (
                <div key={index} className="size-1.5 bg-black rounded-full" />
            ))}
        </div>
    );

    return (
        <>
            {isLoading && <SettlementsElementSkeleton isMobile={isMobile} />}
            {!isLoading && (
                <div
                    className={cn(
                        'p-6 w-full flex items-center gap-4 border border-popover-100 rounded-xl bg-popover-10 select-none truncate transition-shadow duration-300',
                        isMobile && 'p-4',
                        type === 'select' && 'hover:shadow-custom cursor-pointer',
                    )}
                    onClick={type === 'select' ? () => onClick?.() : undefined}
                >
                    <div
                        className={`size-8 shrink-0 flex items-center justify-center rounded-semi-xl ${iconBgColor}`}
                    >
                        <IconComponent className={iconColor} />
                    </div>
                    <div className="flex flex-col truncate">
                        {balanceVisibility === false ? (
                            hiddenBalance
                        ) : (
                            <span
                                className={cn(
                                    'text-base font-medium truncate',
                                    isMobile && 'text-sm',
                                )}
                            ><CurrencyValue value={value} asset={asset} /></span>
                        )}
                        <span className="text-sm text-popover-600 truncate">{title}</span>
                    </div>
                    {type === 'select' && (
                        <Button
                            variant="outline"
                            className="ml-auto p-1.5 bg-trasparent hover:shadow-none active:bg-trasparent"
                        >
                            <ArrowRightIcon />
                        </Button>
                    )}
                    {type === 'pay' && (
                        <Button
                            variant="outline"
                            className="ml-auto px-3.5 py-1.5"
                            onClick={onClick}
                        >
                            <CreditCardIcon />
                            <span>{t('main.finances.components.balance.payButton')}</span>
                        </Button>
                    )}
                </div>
            )}
        </>
    );
};

export default SettlementsElement;
